<template>
  <div class="asetl">
    <section class="sui-card sui-card--flush asetl__board" data-tour="admin-settle-list">
      <div class="asetl__sticky">
        <nav class="asetl__filters" data-tour="admin-settle-kpis" aria-label="정산 요청 상태">
          <button
            type="button"
            class="asetl__filter"
            :class="{ 'is-active': !status }"
            @click="setStatus('')"
          >
            <span class="asetl__filter-label">전체</span>
          </button>
          <button
            type="button"
            class="asetl__filter"
            :class="{ 'is-active': status === 'PAID' }"
            @click="setStatus('PAID')"
          >
            <span class="asetl__filter-label">완료</span>
          </button>
          <button
            type="button"
            class="asetl__filter"
            :class="{ 'is-active': status === 'REQUESTED', 'is-alert': requestedCount > 0 }"
            @click="setStatus('REQUESTED')"
          >
            <span class="asetl__filter-label">신청</span>
          </button>
          <span class="asetl__filters-spacer" />
          <button type="button" class="sui-btn" data-tour="admin-settle-logs" @click="openLogs()">
            <AppIcon icon="mdi-history" :size="16" />
            처리 이력
          </button>
          <span class="asetl__pending-sum" :class="{ 'is-alert': requestedSum > 0 }">
            <AppIcon icon="mdi-cash-clock" :size="16" />
            <span class="asetl__pending-sum-body">
              <em>신청 지급액</em>
              <strong>{{ formatWon(requestedSum) }}원</strong>
            </span>
          </span>
        </nav>

        <p class="asetl__guide">
          판매업체가 고른 구매확정 건이 묶음으로 신청됩니다. 내용을 확인한 뒤 정산하고 「완료」를 누르면 그 신청은 끝납니다.
        </p>

        <div class="sui-toolbar" data-tour="admin-settle-toolbar">
          <select v-model="period" class="sui-select" aria-label="요청 연월" @change="reload">
            <option value="">전체 기간</option>
            <option v-for="option in periodOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          <label class="sui-search">
            <AppIcon icon="mdi-magnify" :size="17" />
            <input
              v-model.trim="keyword"
              type="search"
              placeholder="판매업체 · 신청 이름 · 주문번호"
              @keyup.enter="reload"
            />
          </label>
          <button type="button" class="sui-btn" :disabled="loading" @click="reload">
            <AppIcon icon="mdi-magnify" :size="16" />
            검색
          </button>
        </div>

        <p v-if="errorMessage" class="asetl__error">
          <AppIcon icon="mdi-alert-outline" :size="16" />
          {{ errorMessage }}
        </p>
        <p v-if="requests.length" class="asetl__list-count">{{ requests.length }}묶음</p>
      </div>

      <div class="asetl__scroll">
        <div v-if="requests.length" class="asetl__table-wrap">
          <table class="sui-table asetl__table">
            <thead>
              <tr>
                <th>판매업체</th>
                <th>정산 신청</th>
                <th class="is-num">건수</th>
                <th class="is-num">지급액</th>
                <th>요청일시</th>
                <th>상태</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in requests" :key="row.id">
                <tr>
                  <td>
                    <span class="sui-table__strong">{{ row.storeName }}</span>
                    <span class="sui-table__sub">
                      {{ row.settleBank }} {{ row.settleAccount }}
                      <template v-if="row.settleHolder"> · {{ row.settleHolder }}</template>
                    </span>
                  </td>
                  <td>
                    <span class="sui-table__strong">{{ row.title }}</span>
                  </td>
                  <td class="is-num">{{ row.count }}건</td>
                  <td class="is-num sui-table__strong">{{ formatWon(row.payout) }}원</td>
                  <td>{{ formatDateTime(row.requestedAt) }}</td>
                  <td>
                    <template v-if="row.kind === 'LEGACY'">
                      <span class="sui-badge">이전 정산</span>
                      <span class="sui-table__sub">{{ formatDateTime(row.paidAt) }}</span>
                    </template>
                    <template v-else-if="row.status === 'PAID'">
                      <span class="sui-badge sui-badge--green">완료</span>
                      <span class="sui-table__sub">
                        {{ formatDateTime(row.paidAt) }}
                        <template v-if="row.paidBy"> · {{ row.paidBy }}</template>
                      </span>
                    </template>
                    <span v-else class="sui-badge sui-badge--amber">신청</span>
                  </td>
                  <td class="asetl__row-actions">
                    <button type="button" class="sui-btn sui-btn--sm" @click="toggleBundle(row.id)">
                      {{ opened === row.id ? '접기' : '건별 보기' }}
                    </button>
                    <button type="button" class="sui-btn sui-btn--sm" @click="onExcel(row)">엑셀</button>
                    <button
                      v-if="row.kind !== 'LEGACY' && row.status === 'REQUESTED'"
                      type="button"
                      class="sui-btn sui-btn--sm sui-btn--primary"
                      data-tour="admin-settle-pay"
                      :disabled="busy"
                      @click="onPay(row)"
                    >
                      완료
                    </button>
                    <button
                      v-else-if="row.kind !== 'LEGACY'"
                      type="button"
                      class="sui-btn sui-btn--sm"
                      data-tour="admin-settle-reopen"
                      :disabled="busy"
                      @click="openUnsettle(row)"
                    >
                      완료 해제
                    </button>
                  </td>
                </tr>
                <tr v-if="opened === row.id">
                  <td colspan="7">
                    <table class="asetl__lines">
                      <thead>
                        <tr>
                          <th>주문번호</th>
                          <th class="is-num">판매대금</th>
                          <th class="is-num">수수료</th>
                          <th class="is-num">배송비</th>
                          <th class="is-num">지급액</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in row.items" :key="item.id">
                          <td>
                            {{ item.orderCode }}
                            <span class="sui-table__sub">
                              {{ item.productName }}
                              <template v-if="item.optionLabel"> · {{ item.optionLabel }}</template>
                              · {{ item.qty }}{{ item.unit || '개' }}
                            </span>
                          </td>
                          <td class="is-num">{{ formatWon(item.salesAmount) }}원</td>
                          <td class="is-num">-{{ formatWon(item.platformFee) }}원</td>
                          <td class="is-num">{{ formatWon(item.shippingFee) }}원</td>
                          <td class="is-num">{{ formatWon(item.payout) }}원</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-else class="sui-empty">
          <AppIcon icon="mdi-cash-off" :size="30" />
          <p>{{ loading ? '불러오는 중입니다…' : '정산 신청이 없습니다.' }}</p>
        </div>
      </div>
    </section>

    <div v-if="unsettleOpen" class="asetl__modal" @click.self="closeUnsettle">
      <form class="asetl__reopen-dialog" @submit.prevent="submitUnsettle">
        <h3>완료 해제</h3>
        <p class="asetl__reopen-sub">
          {{ unsettleTarget?.title }} 을 다시 신청 상태로 되돌립니다.
        </p>
        <label class="sui-field">
          <span>해제 사유 <em>*</em></span>
          <textarea
            v-model.trim="unsettleReason"
            class="sui-input"
            rows="4"
            maxlength="255"
            placeholder="예: 송금 오류로 재처리 필요"
            required
          />
        </label>
        <div class="asetl__panel-actions">
          <button type="button" class="sui-btn" @click="closeUnsettle">닫기</button>
          <button
            type="submit"
            class="sui-btn sui-btn--primary"
            :disabled="unsettleReason.trim().length < 5 || busy"
          >
            {{ busy ? '처리 중…' : '완료 해제' }}
          </button>
        </div>
      </form>
    </div>

    <div v-if="logsOpen" class="asetl__modal" @click.self="closeLogs">
      <div class="asetl__logs-dialog" data-tour="admin-settle-log-panel">
        <header class="asetl__logs-head">
          <div>
            <h3>정산 처리 이력</h3>
            <p>최근 처리 전체</p>
          </div>
          <button type="button" class="asetl__close" aria-label="닫기" @click="closeLogs">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>
        <div class="asetl__logs-body">
          <p v-if="logsLoading" class="asetl__logs-empty">불러오는 중…</p>
          <p v-else-if="logsError" class="asetl__logs-empty is-error">{{ logsError }}</p>
          <p v-else-if="!logs.length" class="asetl__logs-empty">이력이 없습니다.</p>
          <table v-else class="asetl__logs-table">
            <thead>
              <tr>
                <th>일시</th>
                <th>구분</th>
                <th>처리자</th>
                <th>주문</th>
                <th class="is-num">지급액</th>
                <th>사유</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td>{{ formatDateTime(log.at) }}</td>
                <td>{{ log.action === 'UNSETTLE' ? '해제' : '정산' }}</td>
                <td>{{ log.actor || '—' }}</td>
                <td>
                  <strong>{{ log.orderCode }}</strong>
                  <span class="sui-table__sub">
                    {{ log.storeName }}
                    <template v-if="log.productName"> · {{ log.productName }}</template>
                  </span>
                </td>
                <td class="is-num">{{ formatWon(log.payout) }}원</td>
                <td>{{ log.reason || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="asetl__panel-actions">
          <span class="asetl__bulk-spacer" />
          <button type="button" class="sui-btn" @click="closeLogs">닫기</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { formatWon } from '@/constants/sellerData'
import {
  downloadAdminSettlementRequestExcel,
  fetchAdminSettlementLogs,
  fetchAdminSettlementRequests,
  markAdminSettlementRequestPaid,
  reopenAdminSettlementRequest,
  type AdminSettlementLog,
  type SettlementRequest,
} from '@/api/mallOrder'

const { notify } = useAdminProducts()

const requests = ref<SettlementRequest[]>([])
const requestedCount = ref(0)
const requestedSum = ref(0)
const loading = ref(false)
const errorMessage = ref('')
const period = ref('')
const status = ref('')
const keyword = ref('')
const busy = ref(false)
const opened = ref(0)

const unsettleOpen = ref(false)
const unsettleReason = ref('')
const unsettleTarget = ref<SettlementRequest | null>(null)

const logsOpen = ref(false)
const logsLoading = ref(false)
const logsError = ref('')
const logs = ref<AdminSettlementLog[]>([])

const periodOptions = computed(() => {
  const set = new Set<string>()
  const now = new Date()
  for (let i = 0; i < 12; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
    set.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`)
  }
  if (period.value) set.add(period.value)
  return [...set].sort((a, b) => b.localeCompare(a))
})

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function toggleBundle(id: number) {
  opened.value = opened.value === id ? 0 : id
}

function setStatus(next: string) {
  if (status.value === next) return
  status.value = next
  void reload()
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await fetchAdminSettlementRequests({
      period: period.value,
      status: status.value,
      keyword: keyword.value,
    })
    requests.value = result.requests
    requestedCount.value = result.requestedCount
    requestedSum.value = result.requestedSum
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '정산 요청을 불러오지 못했습니다.'
    requests.value = []
  } finally {
    loading.value = false
  }
}

async function onExcel(row: SettlementRequest) {
  try {
    await downloadAdminSettlementRequestExcel(row)
  } catch (error) {
    notify(error instanceof Error ? error.message : '엑셀을 내려받지 못했습니다.')
  }
}

async function onPay(row: SettlementRequest) {
  if (busy.value) return
  const ok = await confirmDialog({
    title: '정산 완료',
    message: '이 정산 신청을 완료로 표시할까요?',
    detail: `${row.storeName}\n${row.title}\n${row.count}건 · 지급액 ${formatWon(row.payout)}원`,
    confirmLabel: '완료',
    tone: 'primary',
  })
  if (!ok) return
  busy.value = true
  try {
    notify(await markAdminSettlementRequestPaid(row.id))
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '처리에 실패했습니다.')
  } finally {
    busy.value = false
  }
}

function openUnsettle(row: SettlementRequest) {
  unsettleTarget.value = row
  unsettleReason.value = ''
  unsettleOpen.value = true
}

function closeUnsettle() {
  if (busy.value) return
  unsettleOpen.value = false
  unsettleTarget.value = null
  unsettleReason.value = ''
}

async function submitUnsettle() {
  const target = unsettleTarget.value
  const reason = unsettleReason.value.trim()
  if (!target || busy.value || reason.length < 5) return
  busy.value = true
  try {
    notify(await reopenAdminSettlementRequest(target.id, reason))
    unsettleOpen.value = false
    unsettleTarget.value = null
    unsettleReason.value = ''
    await reload()
  } catch (error) {
    notify(error instanceof Error ? error.message : '처리에 실패했습니다.')
  } finally {
    busy.value = false
  }
}

async function openLogs() {
  logsOpen.value = true
  logsLoading.value = true
  logsError.value = ''
  logs.value = []
  try {
    logs.value = await fetchAdminSettlementLogs({ limit: 100 })
  } catch (error) {
    logsError.value = error instanceof Error ? error.message : '이력을 불러오지 못했습니다.'
  } finally {
    logsLoading.value = false
  }
}

function closeLogs() {
  logsOpen.value = false
}

onMounted(() => {
  void reload()
})
</script>

<style lang="scss" src="./AdminSettlementsPage.scss"></style>
